import { clearTokens, getAccessToken } from '@/utils/auth-token';
import { getAuthGeneration } from '@/utils/auth-teardown';
import { resolveChainIdentityCapability } from '@/utils/chain-identity';
import {
  isSilentCustodialBindPending,
  scheduleSilentPrivyBindAfterFirstDeposit,
} from '@/utils/privy-platform-login';
import { ensureUserSessionProfile } from '@/utils/user-session-profile';
import {
  sessionExpectsCustodialWallet,
  sessionExpectsSelfCustodyWallet,
  usesCustodialExecChannel,
} from '@/utils/session-login-track';

export {
  sessionExpectsCustodialWallet,
  sessionExpectsSelfCustodyWallet,
  usesCustodialExecChannel,
};

/**
 * 托管 Session 的资金门禁：确认 users 投影上已有 type=2 交易通道。
 * WALLET Session 直接放行，不读取 trade-address/list、不要求绑定。
 * @returns {Promise<{
 *   ok: boolean,
 *   code?: 'LIST_FAILED' | 'NOT_BOUND' | 'BINDING',
 *   capability?: object,
 * }>}
 */
export async function checkCustodialMoneyOpsGate() {
  const token = getAccessToken();
  const generation = getAuthGeneration();
  const isCurrent = () => getAccessToken() === token && getAuthGeneration() === generation;
  await ensureUserSessionProfile();
  if (!isCurrent()) return { ok: false, code: 'NOT_BOUND' };
  if (!sessionExpectsCustodialWallet()) {
    return { ok: true };
  }
  if (isSilentCustodialBindPending()) {
    return { ok: false, code: 'BINDING' };
  }
  let capability = await resolveChainIdentityCapability();
  if (!isCurrent()) return { ok: false, code: 'NOT_BOUND' };
  if (capability.listFailed) {
    return { ok: false, code: 'LIST_FAILED', capability };
  }
  if (!capability.hasCustodial) {
    const restored = await scheduleSilentPrivyBindAfterFirstDeposit();
    if (!isCurrent()) return { ok: false, code: 'NOT_BOUND' };
    capability = await resolveChainIdentityCapability();
    if (!isCurrent()) return { ok: false, code: 'NOT_BOUND' };
    if (capability.listFailed) return { ok: false, code: 'LIST_FAILED', capability };
    if (!capability.hasCustodial) {
      return { ok: false, code: restored?.preparing ? 'BINDING' : 'NOT_BOUND', capability };
    }
  }
  return { ok: true, capability };
}

/** 门禁失败 → Error.code，供 i18n / map*Error 识别 */
export async function assertCustodialReadyForMoneyOps() {
  const gate = await checkCustodialMoneyOpsGate();
  if (gate.ok) return gate;
  const err = new Error(
    gate.code === 'LIST_FAILED'
      ? 'TRADE_ADDRESS_LIST_FAILED'
      : gate.code === 'BINDING'
      ? 'CUSTODIAL_WALLET_BINDING'
      : 'CUSTODIAL_WALLET_NOT_BOUND'
  );
  err.code =
    gate.code === 'LIST_FAILED'
      ? 'TRADE_ADDRESS_LIST_FAILED'
      : gate.code === 'BINDING'
      ? 'CUSTODIAL_WALLET_BINDING'
      : 'CUSTODIAL_WALLET_NOT_BOUND';
  throw err;
}

/**
 * 后端业务 msg / 前端门禁 code → 统一文案 key（禁止一律「稍后重试」）
 * @returns {string} i18n key 或 ''
 */
export function resolveCustodialBindErrorI18nKey(err) {
  const code = String(err?.code ?? '').trim();
  if (code === 'CUSTODIAL_WALLET_BINDING' || code === 'BINDING') {
    return 'authDialog.custodialBindingInProgress';
  }
  if (code === 'CUSTODIAL_WALLET_NOT_BOUND' || code === 'NOT_BOUND') {
    return 'authDialog.custodialChannelRequired';
  }
  if (code === 'TRADE_ADDRESS_LIST_FAILED' || code === 'LIST_FAILED') {
    return 'authDialog.custodialListFailed';
  }
  if (
    ['AUTH_SESSION_CHANGED', 'CUSTODIAL_IDENTITY_CONFLICT', 'PRIVY_SIGNER_MISMATCH'].includes(code)
  ) {
    return 'authDialog.custodialIdentityMismatch';
  }
  const raw = String(err?.message ?? err?.msg ?? err ?? '');
  if (/Privy\s*托管钱包未绑定|托管钱包未绑定/i.test(raw)) {
    return 'authDialog.custodialNotBoundApi';
  }
  if (/Privy\s*钱包无效|校验失败/i.test(raw)) {
    return 'authDialog.custodialWalletInvalid';
  }
  if (/链上问题未就绪/i.test(raw)) {
    return 'account.trades.redeemChainNotReady';
  }
  if (/问题链记录不存在|记录不存在:\s*问题链/i.test(raw)) {
    return 'account.trades.redeemChainRecordMissing';
  }
  if (
    /CUSTODIAL_WALLET_NOT_BOUND|TRADE_ADDRESS_LIST_FAILED|CUSTODIAL_WALLET_BINDING|CUSTODIAL_IDENTITY_CONFLICT|PRIVY_SIGNER_MISMATCH/i.test(
      raw
    )
  ) {
    if (raw.includes('IDENTITY_CONFLICT') || raw.includes('SIGNER_MISMATCH')) {
      return 'authDialog.custodialIdentityMismatch';
    }
    if (raw.includes('BINDING')) return 'authDialog.custodialBindingInProgress';
    return raw.includes('LIST')
      ? 'authDialog.custodialListFailed'
      : 'authDialog.custodialChannelRequired';
  }
  return '';
}

export function mapCustodialBindErrorMessage(err, t) {
  const key = resolveCustodialBindErrorI18nKey(err);
  return key ? t(key) : '';
}

/** 门禁失败且需强制重登绑托管时：清 session 并同步登录态（调用方再打开登录窗） */
export async function teardownBrokenCustodialSession() {
  try {
    const { logoutPrivyIfSession } = await import('@/utils/privy');
    await logoutPrivyIfSession();
  } catch {
    /* ignore */
  }
  try {
    const { performLocalLogout } = await import('@/utils/auth-session');
    performLocalLogout();
  } catch {
    clearTokens();
  }
}
